/*
  TranscriptSuggestionPill — compact chip beside a field label showing
  that a transcript suggestion exists for that destination.

  Behaviour (spec §6 field-side surfacing):
    * Violet chip when there is a pending suggestion with no conflict.
    * Amber chip when the LLM's proposal conflicts with an existing
      confirmed value — always opens the comparison dialog.
    * Rose-toned "clarify" chip when a Class D row is missing units /
      side / setup — always opens the comparison dialog so the
      clinician can supply the missing metadata.
    * ✓ / ✕ micro-buttons for one-click accept / reject when
      appropriate (never shown on conflict/incomplete rows).
    * 🔊 opens the evidence drawer with the audio timestamp.
    * ⚑ opens the transcription-error report dialog.
*/
import { Mic } from "lucide-react";


export default function TranscriptSuggestionPill({
  suggestion,
  onAccept,
  onOpenComparison,
  onReject,
  onReport,
  onOpenEvidence,
}) {
  const conflict = suggestion?.policy_result?.validation === "conflict";
  const incomplete = suggestion?.policy_result?.validation === "incomplete";
  const speaker = suggestion?.assertion?.speaker || "unknown";
  const preview = String(suggestion?.proposed_value ?? "").slice(0, 90);
  const tone = incomplete
    ? "border-amber-400 bg-amber-100 text-amber-950 hover:bg-amber-200"
    : conflict
    ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
    : "border-violet-300 bg-violet-50 text-violet-900 hover:bg-violet-100";
  const title = incomplete
    ? `Class D — needs structured metadata. Click to compare + clarify.\n\nProposed (from ${speaker}): "${preview}"`
    : conflict
    ? `Conflicts with an existing value. Click to compare sources.\n\nProposed (from ${speaker}): "${preview}"`
    : `Suggested from ${speaker}. Click to open comparison, or use ✓ to accept.\n\nProposed: "${preview}"`;
  const chipLabel = incomplete
    ? "clarify needed"
    : conflict
    ? "compare sources"
    : "suggested from transcript";
  return (
    <span
      className="inline-flex items-center gap-1"
      data-testid={`transcript-suggest-pill-${suggestion.field_key}`}
    >
      <button
        type="button"
        onClick={(e) => {
          if (conflict || incomplete || e.ctrlKey || e.metaKey) {
            onOpenComparison?.(suggestion.id);
            return;
          }
          onAccept?.(suggestion);
        }}
        title={title}
        className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-0.5 transition-colors ${tone}`}
      >
        <Mic className="h-2.5 w-2.5" />
        {chipLabel}
      </button>
      {!conflict && !incomplete && (
        <>
          <button
            type="button"
            onClick={() => onAccept?.(suggestion)}
            title="Accept this suggestion"
            className="text-2xs px-1 py-0.5 rounded border border-violet-300 bg-white text-violet-800 hover:bg-violet-100"
            data-testid={`transcript-suggest-accept-${suggestion.field_key}`}
          >
            ✓
          </button>
          <button
            type="button"
            onClick={() => onReject?.(suggestion)}
            title="Reject this suggestion"
            className="text-2xs px-1 py-0.5 rounded border border-zinc-300 bg-white text-zinc-500 hover:bg-zinc-100"
            data-testid={`transcript-suggest-reject-${suggestion.field_key}`}
          >
            ✕
          </button>
        </>
      )}
      <button
        type="button"
        onClick={() => onOpenEvidence?.(suggestion)}
        title="Open evidence — replay the audio moment this suggestion came from"
        className="text-2xs px-1 py-0.5 rounded border border-violet-200 bg-white text-violet-700 hover:bg-violet-50"
        data-testid={`transcript-suggest-evidence-${suggestion.field_key}`}
      >
        🔊
      </button>
      <button
        type="button"
        onClick={() => onReport?.(suggestion)}
        title="Report transcription error — logs feedback for prompt tuning and rejects the suggestion"
        className="text-2xs px-1 py-0.5 rounded border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
        data-testid={`transcript-suggest-report-${suggestion.field_key}`}
      >
        ⚑
      </button>
    </span>
  );
}
